import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { comics, categoryList } from "@/mocks/comics";

const siteUrl = "https://vhzhn02.cn";
const FORM_URL = "https://readdy.ai/api/form/da0kgreth95mubjtugi0";

const stats = [
  { value: "1200+", label: "收录作品" },
  { value: "6", label: "作品分类" },
  { value: "30+", label: "每日更新" },
  { value: "98%", label: "读者好评" },
];

const testimonials = [
  {
    name: "漫友小北",
    role: "热血漫爱好者",
    avatar: "https://readdy.ai/api/search-image?query=minimal%20flat%20cartoon%20avatar%20of%20a%20cheerful%20young%20man%2C%20clean%20simple%20style%2C%20warm%20neutral%20background&width=200&height=200&seq=avatar-xiaobei&orientation=squarish",
    text: "分类很清晰，想找哪部直接搜，省了很多翻找的时间，更新也很及时。",
  },
  {
    name: "橘子汽水",
    role: "恋爱漫爱好者",
    avatar: "https://readdy.ai/api/search-image?query=minimal%20flat%20cartoon%20avatar%20of%20a%20friendly%20young%20woman%20with%20long%20hair%2C%20clean%20simple%20style%2C%20warm%20pastel%20background&width=200&height=200&seq=avatar-juzi&orientation=squarish",
    text: "页面清爽不杂乱，章节目录一目了然，追更体验非常棒，值得推荐。",
  },
  {
    name: "夜行侦探",
    role: "悬疑漫爱好者",
    avatar: "https://readdy.ai/api/search-image?query=minimal%20flat%20cartoon%20avatar%20of%20a%20thoughtful%20man%20wearing%20glasses%2C%20clean%20simple%20style%2C%20cool%20neutral%20background&width=200&height=200&seq=avatar-zhentan&orientation=squarish",
    text: "索引整理得很用心，资源汇总全面，站内检索速度也很快，很实用。",
  },
];

export default function About() {
  const [formState, setFormState] = useState<{
    status: "idle" | "loading" | "success" | "error";
    message: string;
  }>({ status: "idle", message: "" });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormState({ status: "success", message: "感谢您的留言，我们会尽快与您联系！" });
      form.reset();
      return;
    }
    // 空蜜罐字段不随请求提交
    formData.delete("website_alt");

    setFormState({ status: "loading", message: "" });
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        (responseText ? responseText : "");
      if (
        res.ok &&
        parsed?.code === "OK" &&
        !String(serverMsg).toLowerCase().includes("spam")
      ) {
        setFormState({ status: "success", message: "感谢您的留言，我们会尽快与您联系！" });
        form.reset();
      } else {
        setFormState({
          status: "error",
          message:
            serverMsg && !serverMsg.toLowerCase().includes("spam")
              ? String(serverMsg)
              : "提交失败，请稍后再试。",
        });
      }
    } catch {
      setFormState({ status: "error", message: "网络异常，请稍后重试。" });
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于漫天堂",
    url: `${siteUrl}/about`,
    isPartOf: { "@type": "WebSite", name: "漫天堂", url: siteUrl },
    about: "漫天堂品牌介绍、口碑与联系方式",
  };

  return (
    <>
      <JsonLd data={[schema]} />
      <Navbar />

      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ name: "关于我们" }]} />

        {/* Brand intro hero */}
        <section className="relative mt-4 overflow-hidden rounded-lg">
          <div className="relative flex min-h-[240px] w-full items-center justify-center md:min-h-[300px]">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20flat%20illustration%20of%20open%20books%20and%20floating%20comic%20pages%20with%20soft%20sunlight%2C%20reading%20community%20theme%2C%20minimal%20modern%20style%2C%20gentle%20gradient%20background&width=1600&height=600&seq=about-hero&orientation=landscape"
              alt="关于漫天堂"
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/50"></div>
            <div className="relative z-10 w-full max-w-2xl px-4 text-center">
              <h1 className="font-heading text-2xl font-black text-background-50 md:text-4xl">
                关于漫天堂
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-background-100 md:text-base">
                一个为漫画爱好者打造的阅读与索引平台，聚合热门连载、经典旧作与最新更新，让每一部好作品都被更多人发现。
              </p>
            </div>
          </div>
        </section>

        {/* Brand intro */}
        <section className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-book-2-fill text-xl" />
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              品牌介绍
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              漫天堂成立于 2023 年，从最初的漫画索引工具，逐步成长为集作品库、全站目录、分类浏览与站内检索于一体的阅读平台。我们相信，好的内容值得被轻松找到。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              目前漫天堂已收录上千部作品，覆盖热血动作、玄幻修真、恋爱日常、悬疑推理、搞笑日常、科幻末世等热门类型，持续整理并更新资源汇总，帮助读者省去逐个翻找的麻烦。
            </p>
          </div>

          {/* Reputation stats */}
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-shield-star-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              平台口碑
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              我们以内容质量与使用体验为准则，持续获得读者的认可与推荐。以下是我们引以为傲的数据：
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg bg-background-100 p-4 text-center">
                  <p className="font-heading text-2xl font-bold text-primary-600">{s.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mt-10">
          <h2 className="mb-5 text-center font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            读者口碑
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-5"
              >
                <div className="flex items-center gap-1 text-secondary-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={`${t.name} 头像`}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mt-10 grid gap-6 lg:grid-cols-2" id="contact">
          {/* Contact info */}
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              无论是建议、合作还是问题反馈，欢迎随时联系我们。我们会认真阅读每一条留言。
            </p>
            <div className="mt-6 flex flex-col gap-4">
              {[
                { icon: "ri-mail-line", label: "邮箱", value: "contact@mantiantang.example.com" },
                { icon: "ri-wechat-line", label: "官方微信", value: "mantiantang_comic" },
                { icon: "ri-time-line", label: "工作时间", value: "周一至周日 9:00 - 22:00" },
                { icon: "ri-map-pin-line", label: "地址", value: "中国 · 创意产业园区 8 号楼" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact form */}
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              给我们留言
            </h2>
            <form
              id="contact-form"
              data-readdy-form="da0kgreth95mubjtugi0"
              onSubmit={handleSubmit}
              className="mt-5 flex flex-col gap-4"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-700">
                    您的称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="如何称呼您"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm text-foreground-700">
                  留言主题
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  placeholder="例如：作品反馈 / 合作咨询"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm text-foreground-700">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请告诉我们您的想法（最多 500 字）"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary-400"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              {/* 反垃圾蜜罐字段（对用户不可见） */}
              <div className="anti-spam-field">
                <label htmlFor="website_alt">Website</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formState.status === "success" && (
                <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-fill" />
                  {formState.message}
                </p>
              )}
              {formState.status === "error" && (
                <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2 text-sm text-primary-900">
                  <i className="ri-error-warning-fill" />
                  {formState.message}
                </p>
              )}

              <button
                type="submit"
                disabled={formState.status === "loading"}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60"
              >
                {formState.status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" /> 提交留言
                  </>
                )}
              </button>
            </form>
          </div>
        </section>

        {/* Category links */}
        <section className="mt-10 rounded-lg border border-background-200/70 bg-background-50 p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">浏览漫画分类</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {categoryList
              .filter((c) => c.key !== "all")
              .map((c) => (
                <span
                  key={c.key}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-700"
                >
                  <i className={`${c.icon} mr-1 text-xs`} />
                  {c.name}（{comics.filter((x) => x.category === c.key).length} 部）
                </span>
              ))}
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}